<template>
  <div class="summary-360">
    <!-- Indicadores -->
    <div class="kpis" data-test="kpis">
      <v-card class="kpi" data-test="kpi-memberships">
        <span class="kpi-label">Membresías vigentes</span>
        <span class="kpi-value">{{ membershipCount.active }} de {{ membershipCount.total }}</span>
        <span class="kpi-sub">{{ membershipSubtext }}</span>
      </v-card>
      <v-card class="kpi" data-test="kpi-refunds">
        <span class="kpi-label">Reembolsos pendientes</span>
        <span class="kpi-value">{{ pendingRefunds }}</span>
        <span class="kpi-sub">Registrados, en revisión o por corregir</span>
      </v-card>
      <v-card class="kpi" :class="{ 'kpi--alert': alerts.length }" data-test="kpi-attention">
        <span class="kpi-label">Requiere atención</span>
        <template v-if="alerts.length">
          <span class="kpi-value kpi-value--text">{{ alerts[0].label }}</span>
          <span class="kpi-sub">
            {{ alerts[0].detail }}<template v-if="alerts.length > 1"> · {{ alerts.length }} alertas</template>
          </span>
        </template>
        <template v-else>
          <span class="kpi-value kpi-value--text kpi-value--ok">Todo en orden</span>
          <span class="kpi-sub">Sin pagos fallidos, vencimientos ni topes por agotarse</span>
        </template>
      </v-card>
    </div>

    <!-- Membresías vigentes y su uso -->
    <v-card class="seccion-card" data-test="usage-section">
      <v-card-title class="section-head">
        <v-icon small>mdi-chart-box-outline</v-icon>Membresías vigentes y su uso
      </v-card-title>

      <v-skeleton-loader v-if="usage.loading" type="table-tbody" class="px-4" data-test="usage-loading" />

      <SourceError360
        v-else-if="usage.error"
        label="el uso de las membresías vigentes"
        @retry="$emit('retry-usage')"
      />

      <div v-else-if="!usageRows.length" class="empty-state" data-test="usage-empty">
        El tutor no tiene membresías vigentes.
        <a href="#" class="empty-link" data-test="go-to-pets" @click.prevent="$emit('go-to', 'mascotas')">
          Ver mascotas y planes
        </a>
      </div>

      <template v-else>
        <div class="table-scroll">
          <table class="usage-table" data-test="usage-table">
            <thead>
              <tr>
                <th scope="col">Mascota · Plan</th>
                <th scope="col">Vigencia</th>
                <th scope="col">Monto cubierto</th>
                <th scope="col">Usos</th>
                <th scope="col">Alertas</th>
                <th scope="col"><span class="sr-only">Acciones</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in usageRows"
                :key="row.membershipId"
                :data-test="`usage-row-${row.membershipId}`"
              >
                <td>
                  <div class="cell-main">{{ row.petName || '—' }}</div>
                  <div class="cell-sub">{{ row.planName || '—' }}</div>
                </td>
                <td>
                  <div class="cell-main pw-num">{{ validityText(row) }}</div>
                </td>
                <td data-test="cell-amount">
                  <template v-if="row.amountCap !== null">
                    <div class="cell-main pw-num">
                      {{ formatMoney(row.amountUsedInCapped, row.currency) }} de {{ formatMoney(row.amountCap, row.currency) }}
                    </div>
                    <v-progress-linear
                      :value="ratioPercent(row.amountUsedInCapped, row.amountCap)"
                      :color="barColor(row.amountUsedInCapped, row.amountCap)"
                      height="6"
                      rounded
                      data-test="amount-bar"
                    />
                  </template>
                  <span v-else-if="row.usesCap !== null" class="cell-sub">Sin tope de monto (solo cupones)</span>
                  <span v-else>—</span>
                </td>
                <td data-test="cell-uses">
                  <!-- Solo el contador del período: el tope de usos es por servicio y sumado no se lee bien. -->
                  <span class="cell-main pw-num">{{ usesText(row.usesCount) }}</span>
                </td>
                <td data-test="cell-alerts">
                  <div class="alert-chips">
                    <v-chip
                      v-for="chip in rowAlertChips(row)"
                      :key="chip.label"
                      small
                      outlined
                      :color="chip.color"
                      class="font-weight-bold"
                    >{{ chip.label }}</v-chip>
                  </div>
                </td>
                <td class="text-right">
                  <v-btn
                    small
                    text
                    color="primary"
                    :aria-label="`Ver uso de ${row.petName || 'la mascota'}`"
                    data-test="view-usage"
                    @click="$emit('view-usage', row.membershipId)"
                  >Ver uso</v-btn>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="usage-footer" data-test="usage-footer">
          Cada membresía muestra su uso en la moneda de su plan; los montos no se suman entre membresías.
        </div>
      </template>
    </v-card>

    <!-- Últimos movimientos: misma tabla que «Uso de coberturas», con los 5 más recientes. -->
    <CoverageUsageTable
      :coverage-usage="coverageUsage"
      :limit="movementsLimit"
      title="Últimos movimientos"
      icon="mdi-history"
      data-test="movements-section"
    >
      <template #actions>
        <a href="#" class="head-link" data-test="go-to-coverage" @click.prevent="$emit('go-to', 'coberturas')">
          Ver historial
        </a>
      </template>
    </CoverageUsageTable>
  </div>
</template>

<script>
import SourceError360 from './SourceError360.vue';
import CoverageUsageTable from './CoverageUsageTable.vue';
import { formatMoney, formatIsoDate } from '@/utils/service-usage.utils';
import {
  countActiveMemberships,
  pendingRefundsCount,
  attentionAlerts,
  daysUntil,
  usageRowEndDate
} from '@/utils/vision360.utils';

const PERCENT = 100;
const NEAR_CAP_RATIO = 0.8;
const EXPIRING_SOON_DAYS = 30;
const MOVEMENTS_LIMIT = 5;

export default {
  name: 'Summary360Tab',
  components: { SourceError360, CoverageUsageTable },
  props: {
    // Membresías de la 360 en cualquier estado (dataMixin.fetchMembershipData).
    detailsPlan: { type: Array, default: () => [] },
    // Reembolsos (dataMixin.fetchRefundsData); `changelog` es el nombre del estado.
    requestUsage: { type: Array, default: () => [] },
    // Catálogo de GET /refunds/changelog/statuses: { name, sequenceOrder, highlightColor }.
    refundStatuses: { type: Array, default: () => [] },
    // Usos de cobertura ya unidos (coverage-usage.utils.buildCoverageUsageRows).
    coverageUsage: { type: Array, default: () => [] },
    // Uso de las membresías vigentes: GET /memberships/360/active-service-usage/:pgId.
    usage: {
      type: Object,
      default: () => ({ loading: false, error: false, data: null })
    },
    // Fecha de referencia para "vence en N días"; por defecto, hoy.
    today: { type: [Date, String], default: null }
  },
  data() {
    return { movementsLimit: MOVEMENTS_LIMIT };
  },
  computed: {
    referenceDate() {
      return this.today || new Date();
    },
    usageRows() {
      return this.usage?.data?.memberships || [];
    },
    membershipCount() {
      return countActiveMemberships(this.detailsPlan, this.usageRows);
    },
    membershipSubtext() {
      const { historic, countries } = this.membershipCount;
      const parts = [historic === 1 ? '1 histórica' : `${historic} históricas`];
      if (countries.length) parts.push(countries.join(', '));
      return parts.join(' · ');
    },
    pendingRefunds() {
      return pendingRefundsCount(this.requestUsage, this.refundStatuses);
    },
    alerts() {
      return attentionAlerts(this.detailsPlan, this.usageRows, this.referenceDate);
    }
  },
  methods: {
    formatMoney,
    formatIsoDate,
    ratioPercent(used, cap) {
      if (!(cap > 0)) return 0;
      return Math.min(PERCENT, Math.round((used / cap) * PERCENT));
    },
    barColor(used, cap) {
      const ratio = cap > 0 ? used / cap : 0;
      if (ratio >= 1) return 'error';
      if (ratio >= NEAR_CAP_RATIO) return 'warning';
      return 'primary';
    },
    // Fin de vigencia: fecha de desactivación si existe; si no, la de expiración (membership-data).
    endDateOf(row) {
      return usageRowEndDate(row, this.detailsPlan);
    },
    // Sin fecha de fin conocida se muestra solo el inicio.
    validityText(row) {
      const start = formatIsoDate(row.startDate);
      const end = this.endDateOf(row);
      if (!end) return `Desde ${start}`;
      return `${start} – ${formatIsoDate(end)}`;
    },
    usesText(count) {
      const uses = Number(count) || 0;
      return uses === 1 ? '1 uso' : `${uses} usos`;
    },
    expiringText(row) {
      const days = daysUntil(this.endDateOf(row), this.referenceDate);
      if (days === null || days < 0 || days > EXPIRING_SOON_DAYS) return '';
      if (days === 0) return 'Vence hoy';
      return days === 1 ? 'Vence en 1 día' : `Vence en ${days} días`;
    },
    // Chips de la columna Alertas: vence en ≤ 30 días, agotados, en carencia y % usado (≥ 80 %).
    // Chips independientes por tipo de alerta: disponibilidad de servicios (siempre uno),
    // carencia y vencimiento (solo si aplican). Una fila puede tener varios a la vez.
    rowAlertChips(row) {
      const chips = [this.availabilityChip(row)];
      if (row.inGracePeriodCount > 0) chips.push({ label: `${row.inGracePeriodCount} en carencia`, color: 'blue' });
      const expiring = this.expiringText(row);
      if (expiring) chips.push({ label: expiring, color: 'orange' });
      return chips;
    },
    // Prioridad: agotado, luego % usado (≥ 80 %), si no «disponible»; un servicio agotado ya está al 100 %.
    availabilityChip(row) {
      if (row.exhaustedCount > 0) {
        const label = row.exhaustedCount === 1 ? '1 agotado' : `${row.exhaustedCount} agotados`;
        return { label, color: 'red' };
      }
      if (row.maxUsageRatio >= NEAR_CAP_RATIO) {
        return { label: `${Math.round(row.maxUsageRatio * PERCENT)} % usado`, color: 'orange' };
      }
      return { label: 'Todo disponible', color: 'green' };
    }
  }
};
</script>

<style scoped>
.kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}
.kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 20px;
}
.kpi--alert {
  border-left: 3px solid var(--pw-error) !important;
}
.kpi-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-muted);
}
.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--pw-text);
  font-variant-numeric: tabular-nums;
}
.kpi-value--text {
  font-size: 1.05rem;
}
.kpi-value--ok {
  color: var(--pw-success);
}
.kpi-sub {
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}

.seccion-card {
  margin-bottom: 20px;
}
.section-head {
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 15px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.section-head .v-icon {
  margin-right: 8px;
  color: var(--pw-text-muted);
}
.head-link,
.empty-link {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--pw-primary);
}

.table-scroll {
  overflow-x: auto;
}
.usage-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.84rem;
}
.usage-table th {
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-muted);
  padding: 10px 16px;
  border-bottom: 1px solid var(--pw-border);
  white-space: nowrap;
}
.usage-table td {
  padding: 10px 16px;
  border-bottom: 1px solid var(--pw-divider);
  vertical-align: middle;
  min-width: 120px;
}
.usage-table tr:last-child td {
  border-bottom: 0;
}
.cell-main {
  font-weight: 500;
  color: var(--pw-text);
}
.cell-sub {
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}
.cell-sub--warning {
  color: var(--pw-warning);
  font-weight: 600;
}
.alert-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.usage-footer {
  padding: 10px 20px;
  border-top: 1px solid var(--pw-divider);
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}
.empty-state {
  padding: 24px 20px;
  color: var(--pw-text-muted);
}


.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
}
</style>
